import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoaderStatus } from 'src/types';

const DEFAULT_TIMEOUT_MS = 20_000;

export const useSdkLoader = (timeoutMs = DEFAULT_TIMEOUT_MS) => {
  const [status, setStatus] = useState<LoaderStatus>('idle');
  const timerRef = useRef<number | null>(null);

  const start = useCallback(() => {
    if (status === 'loading' || status === 'ready' || !!timerRef.current) {
      return;
    }

    setStatus('loading');

    timerRef.current = window.setTimeout(() => {
      setStatus('timeout');
    }, timeoutMs);
  }, [status, timeoutMs]);

  const resolve = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setStatus('ready');
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setStatus('idle');
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  return useMemo(
    () => ({
      status,
      loading: status === 'loading',
      ready: status === 'ready',
      timedOut: status === 'timeout',
      start,
      resolve,
      reset,
    }),
    [status, start, resolve, reset],
  );
};
